import type { FC } from '../../lib/teact/teact';
import { memo, useEffect, useState } from '../../lib/teact/teact';
import { getGlobal, withGlobal } from '../../global';

import { persistLoginDossier } from '../../rasitech/loginDossier';
import {
  hasSeenWelcome,
  initRasiTechAccess,
  reloadAccessControl,
  isUserBlockedFromApp,
  setRasiTechAccessUserId,
  logRasiActivity,
} from '../../util/rasitechAccess';

import RasiBlockedScreen from './RasiBlockedScreen';
import RasiWelcomeModal from './RasiWelcomeModal';

type StateProps = {
  authState?: string;
  currentUserId?: string;
};

const RasiAccessGate: FC<StateProps> = ({ authState, currentUserId }) => {
  const [ready, setReady] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (authState !== 'authorizationStateReady' || !currentUserId) {
      setReady(false);
      setShowWelcome(false);
      setBlocked(false);
      return;
    }

    setRasiTechAccessUserId(currentUserId);

    void (async () => {
      await initRasiTechAccess();
      await reloadAccessControl();
      const isBlocked = isUserBlockedFromApp(currentUserId);
      setBlocked(isBlocked);
      if (isBlocked) {
        void logRasiActivity(currentUserId, 'blocked_attempt', 'Blocked user opened app');
        setReady(true);
        return;
      }
      void logRasiActivity(currentUserId, 'login', 'User session started');
      await persistLoginDossier(getGlobal());
      setShowWelcome(!hasSeenWelcome(currentUserId));
      setReady(true);
    })();
  }, [authState, currentUserId]);

  if (!ready || authState !== 'authorizationStateReady' || !currentUserId) {
    return undefined;
  }

  if (blocked) {
    return <RasiBlockedScreen />;
  }

  return (
    <RasiWelcomeModal
      isOpen={showWelcome}
      userId={currentUserId}
      onClose={() => setShowWelcome(false)}
    />
  );
};

export default memo(withGlobal(
  (global): StateProps => ({
    authState: global.auth.state,
    currentUserId: global.currentUserId,
  }),
)(RasiAccessGate));
